// ####################################################
// # The following were moved from legacy api/imports
// # These types are used only in integrations
// ####################################################

/** Used to identify a person in input operations */
export type PersonIdentityProvider =
  | "DEFAULT" // Only supported for legacy CSV parsers that call imports. DO NOT USE in new code.
  | "EMAIL" // Only unique within a company
  | "EMPLOYEE_ID" // Only unique within a company
  | "GRS_MEMBER_ID"
  | "KAZOO_ID" // globally unique identifier. Kazoo-assigned
  | "LEGACY_HIGHGROUND_ID"
  | "LEGACY_HIGHGROUND_MEMBER_ID"
  | "LEGACY_HIGHGROUND_USERNAME"
  | "LEGACY_PULSE_EMPLOYEE_ID"
  | "LEGACY_PULSE_USER_ID"
  | "LEGACY_YEI_ID"
  | "LEGACY_YEI_USERNAME"
  | "USERNAME";

/** Used to identify a person in input operations */
export interface PersonIdentityInput {
  /** Provider */
  provider: PersonIdentityProvider;
  /** Identifier of their account */
  key: string;
}

/** Nullable to support delete operations. e.g.: Update my manager to null. */
export interface NullablePersonIdentityInput {
  provider: PersonIdentityProvider;
  key?: string | null;
}

/** Used to identify Departments, Locations, and Groups. */
type EntityIdentifierType =
  | "EXTERNAL" // client-provided key
  | "KAZOO_ID" // globally unique, kazoo-generated id
  | "LEGACY_HIGHGROUND_ID" // hg
  | "LEGACY_YEI_ID"; // YEI

/** Identifies an entity like a Location, Group, or Department. */
export interface EntityIdentifierInput {
  type: EntityIdentifierType;
  key: string;
}

/** Identifies an entity like a Location, Group, or Department. Nullable for delete operations. */
export interface NullableEntityIdentifierInput {
  type: EntityIdentifierType;
  key?: string | null;
}

export type GroupLikeEntityTypes =
  | "DEPARTMENT"
  | "GROUP_AFFINITY"
  | "GROUP_PRIVATE"
  | "LOCATION";

/** Optional container for metadata, mostly used to identify the row of a csv file */
export interface TaskMetadataInput {
  location?: string | null;
}

/** Allows us to attach custom attributes to an entity */
export interface PersonTaskCustomAttributeValuesInput {
  archiveMissing?: boolean | null;
  values: PersonTaskCustomAttributeValueInput[];
}

/** Name and value of a custom attribute */
interface PersonTaskCustomAttributeValueInput {
  /**
   * Canonical name of this custom field. If it doesn't exist, we throw a UserInputError.
   */
  name: string;
  /**
   * We pass the value in as a string and parse it at runtime.
   * Passing invalid input, e.g. 'foo' for a number field will throw UserInputError.
   * Passing null removes the custom field from the Person.
   */
  value?: string | null;
}

// Person input
export interface PersonTaskInput {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  preferredName?: string | null;
  initials?: string | null;
  userStatus?: string | null; // default "Active" when used in GQL
  onboardDate?: string | null;
  offboardDate?: string | null;
  offboardType?: string | null;
  employeeType?: string | null;
  employeeId?: string | null;
  username?: string | null;
  loginEmail?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  roles?: string[] | null;
  assignedGroups?: EntityIdentifierInput[] | null;
  department?: NullableEntityIdentifierInput | null;
  location?: NullableEntityIdentifierInput | null;
  assignedManager?: NullablePersonIdentityInput | null;
  /** Assigns a Secondary Manager to a Person. Cannot be the same Person as a manager. */
  assignedSecondaryManager?: NullablePersonIdentityInput | null;
  aboutMe?: string | null;
  birthDate?: string | null;
  /** ISO 3166 numeric code (e.g. 840), ISO 3166-Alpha-2 (e.g. US) ISO 3166-Alpha-3 (e.g. USA) */
  country?: string | null;
  customPointRule?: string | null;
  /** The points to assign the user for redemption. This value will be validated as a number */
  redemptionPoints?: string | null;
  /** The points to assign the user for gifting. This value will be validated as a number */
  giftingPoints?: string | null;
  suppressAnniversaryVisibility?: boolean | null;
  suppressBirthdateVisibility?: boolean | null;
  /** Either a url of a publicly-accessible image or a Data url in the format of data:text/plain;base64,[DATA HERE] */
  profileImage?: string | null;
  localeSetting?: string | null;
  externalHrisId?: string | null;
  /** Optional container for metadata, mostly used to identify the row of a csv file */
  metadata?: TaskMetadataInput | null;
  /** The YEI UserId. Used for migrating users from legacy systems. */
  legacyYeiUserId?: string | null;
  /** The HighGround userId. Used for migrating users from legacy systems. */
  legacyHighGroundUserId?: string | null;
  /** The HighGround MemberId. Used for migrating users from legacy systems. */
  legacyHighGroundMemberId?: string | null;
  customAttributes?: PersonTaskCustomAttributeValuesInput | null;
  /** The Pulse Employee ID. Used for migrating users from legacy systems. */
  legacyPulseEmployeeId?: string | null;
}

// Group-like input
export interface GroupLikeTaskInput {
  /**
   * Optional identifier to say, `create or update an entity with this KAZOO_ID or LEGACY_YEI_ID or other supported identifier type.`
   * If not provided, we fall back to key.
   */
  identifier?: EntityIdentifierInput | null;
  /** External identifier for this input, typically an HRIS system identifier */
  key?: string | null;
  /** Name of the department, location, or group. On creation, if null we default first to key, then identifier.key */
  name?: string | null;
  parent?: NullableEntityIdentifierInput | null;
  owners?: PersonIdentityInput[] | null;
  administrators?: PersonIdentityInput[] | null;
  type: GroupLikeEntityTypes;
  legacyPmId?: string | null;
  legacyRrId?: string | null;
  /** Optional container for metadata, mostly used to identify the row of a csv file */
  metadata?: TaskMetadataInput | null;
}

export interface GoalTaskInput {
  /** Goal external id */
  externalKey?: string | null;
  /** Person Kazoo ID, employee ID, or name of the person who owns this goal. */
  ownerId?: string | null;
  /** Department Kazoo ID, external key, or name of the department this goal belongs to, if applicable */
  departmentId?: string | null;
  /** Goal name */
  name?: string | null;
  /** Goal category name */
  category?: string | null;
  archived?: boolean | null;
  goalType?: string | null;
  status?: string | null;
  riskStatus?: string | null;
  description?: string | null;
  measurableResults?: GoalMeasurableResultTaskInput[] | null;
  dueDate: unknown; // Date scalar (ISO-8601, date-only)
  startDate?: unknown; // Date scalar (ISO-8601, date-only)
  privacy?: string | null;
  /** Person Kazoo ID, employee ID, or name of the person who collaborates on this goal. */
  collaborators?: (string | null)[] | null;
  /** External Key or goal ID of the goal that this goal aligns to */
  parentId?: string | null;
  /** No metadata for Goals yet, but it is required for type safety */
  metadata: TaskMetadataInput | null;
}

export interface GoalMeasurableResultTaskInput {
  id?: string | null; // ID scalar
  type?: string | null;
  name?: string | null;
  description?: string | null;
  dueDate?: string | null; // Date scalar (ISO-8601, date-only)
  target?: number | null;
  progress?: number | null;
  progressText?: string | null;
}

export type ImportBatchOption =
  | "CREATE_INACTIVE_USERS"
  | "ARCHIVE_MISSING_USERS"
  | "CREATE_ACTIVATION_REQUEST_ON_CREATION";
